import { useEffect, useState, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

const C = {
  bg: '#0f0f0f',
  card: '#1a1a1a',
  border: '#2a2a2a',
  tx: '#e5e5e5',
  mt: '#777',
  ac: '#3b82f6',
  red: '#ef4444',
  green: '#10b981',
};

async function fetchMe() {
  const r = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`auth/me: HTTP ${r.status}`);
  return r.json();
}

async function postLogin(email, password) {
  const r = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (r.status === 401) {
    return { ok: false, error: 'invalid_credentials' };
  }
  if (!r.ok) {
    return { ok: false, error: `HTTP ${r.status}` };
  }
  const user = await r.json();
  return { ok: true, user };
}

async function postChangePassword(currentPassword, newPassword) {
  const r = await fetch(`${API_BASE}/auth/change-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (r.ok) return { ok: true };
  let body = null;
  try { body = await r.json(); } catch {}
  return { ok: false, error: body?.error || `HTTP ${r.status}` };
}

async function postLogout() {
  await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function useAuth() {
  const [state, setState] = useState({ status: 'loading', user: null, error: null });

  const refresh = useCallback(async () => {
    // Do NOT transition to 'loading' on re-checks. The initial mount
    // already starts in 'loading' state; subsequent refresh() calls (on
    // tab focus, after sign-in, etc.) verify the session in the
    // background and only flip status when the new value is known.
    // Blanking to 'loading' here unmounts whatever the user is on —
    // LoginScreen, ChangePasswordScreen, or the App — and that wipes
    // form input state on every alt-tab.
    try {
      const me = await fetchMe();
      if (me) setState({ status: 'authenticated', user: me, error: null });
      else setState({ status: 'unauthenticated', user: null, error: null });
    } catch (e) {
      setState({ status: 'error', user: null, error: e.message || String(e) });
    }
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    const onExpired = () => setState({ status: 'unauthenticated', user: null, error: null });
    window.addEventListener('focus', onFocus);
    window.addEventListener('ir:auth-expired', onExpired);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ir:auth-expired', onExpired);
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    await postLogout();
    setState({ status: 'unauthenticated', user: null, error: null });
  }, []);

  return { ...state, refresh, logout };
}

const formStyle = {
  width: 360,
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: 24,
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: `1px solid ${C.border}`,
  background: C.bg,
  color: C.tx,
  fontSize: 13,
  marginBottom: 12,
  boxSizing: 'border-box',
  outline: 'none',
};

const buttonStyle = (disabled) => ({
  width: '100%',
  padding: '9px 12px',
  borderRadius: 7,
  border: 'none',
  background: disabled ? C.mt : C.ac,
  color: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: disabled ? 'wait' : 'pointer',
});

const labelStyle = { fontSize: 11, color: C.mt, display: 'block', marginBottom: 4 };

const screenWrap = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: C.bg,
  color: C.tx,
  fontFamily: 'system-ui,sans-serif',
  padding: 24,
};

const Brand = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: 12,
        color: '#fff',
      }}
    >
      IR
    </div>
    <div style={{ fontWeight: 700, fontSize: 15 }}>Importrust Finance</div>
  </div>
);

function LoginScreen({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || busy) return;
    setBusy(true);
    setErr(null);
    const r = await postLogin(email.trim().toLowerCase(), password);
    setBusy(false);
    if (!r.ok) {
      setErr(r.error === 'invalid_credentials' ? 'Wrong email or password.' : r.error);
      return;
    }
    onSignedIn(r.user);
  };

  return (
    <div style={screenWrap}>
      <form onSubmit={submit} style={formStyle}>
        <Brand />
        <label style={labelStyle}>Email</label>
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@importrust.com"
          style={inputStyle}
        />
        <label style={labelStyle}>Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
        <button type="submit" disabled={busy} style={buttonStyle(busy)}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {err && <div style={{ fontSize: 12, color: C.red, marginTop: 10 }}>{err}</div>}
      </form>
    </div>
  );
}

function ChangePasswordScreen({ onDone }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (next !== confirm) { setErr('New passwords do not match.'); return; }
    if (next.length < 12) { setErr('Password must be at least 12 characters.'); return; }
    setBusy(true);
    const r = await postChangePassword(current, next);
    setBusy(false);
    if (!r.ok) {
      if (r.error === 'invalid_current_password') setErr('Current password is wrong.');
      else if (typeof r.error === 'string' && r.error.startsWith('password_too_short')) {
        setErr('Password must be at least 12 characters.');
      } else {
        setErr(r.error);
      }
      return;
    }
    onDone();
  };

  return (
    <div style={screenWrap}>
      <form onSubmit={submit} style={formStyle}>
        <Brand />
        <div style={{ fontSize: 12, color: C.mt, marginBottom: 14, lineHeight: 1.5 }}>
          Set a new password to continue. Minimum 12 characters.
        </div>
        <label style={labelStyle}>Current password</label>
        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} style={inputStyle} autoFocus />
        <label style={labelStyle}>New password</label>
        <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} style={inputStyle} />
        <label style={labelStyle}>Confirm new password</label>
        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} style={inputStyle} />
        <button type="submit" disabled={busy} style={buttonStyle(busy)}>
          {busy ? 'Saving…' : 'Save and continue'}
        </button>
        {err && <div style={{ fontSize: 12, color: C.red, marginTop: 10 }}>{err}</div>}
      </form>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ ...screenWrap, color: C.mt, fontSize: 13 }}>Loading…</div>
  );
}

// AuthGate: gates rendering on session state. Accepts either a normal
// child node or a render-prop function that receives `{user, logout}`,
// so the wrapped App can render the user's email + sign-out inline in
// its own header without using a floating overlay.
export function AuthGate({ children }) {
  const { status, user, refresh, logout } = useAuth();

  if (status === 'loading') return <LoadingScreen />;
  if (status === 'unauthenticated' || status === 'error') {
    return <LoginScreen onSignedIn={() => refresh()} />;
  }
  if (user?.mustChangePassword) {
    return <ChangePasswordScreen onDone={() => refresh()} />;
  }
  return typeof children === 'function' ? children({ user, logout }) : children;
}
